import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { allPageFaqs } from "@/lib/section-faqs";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { ES_SEMAGLUTIDE, drugAboutAndMainEntity, drugNodes } from "@/lib/drug-schema";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Necesito receta para la semaglutida?",
    a: "Sí. La semaglutida es un medicamento de receta. En Virginia Bariatric Surgery empezamos con una evaluación médica, en persona en Herndon o por telemedicina, que revisa su historia clínica, sus análisis de laboratorio, sus intentos previos de bajar de peso y sus metas. Si la semaglutida es apropiada, la recetamos y lo vemos con regularidad para ajustar la dosis.",
  },
  {
    q: "¿Es segura la semaglutida compuesta o «compounded»?",
    a: "Solo recetamos semaglutida aprobada por la FDA (Wegovy, Ozempic). Ahora que terminó la declaración de escasez, ya no se permite la producción masiva de versiones compuestas, y esos productos nunca fueron revisados por la FDA en cuanto a seguridad, pureza o exactitud de la dosis. Si un precio parece demasiado bueno para ser cierto, pregunte qué contiene realmente el frasco.",
  },
  {
    q: "¿Recuperaré el peso si dejo la semaglutida?",
    a: "Sin un plan de mantenimiento, la mayoría de los pacientes recupera una parte importante del peso perdido después de suspenderla; en las extensiones de los estudios, alrededor de dos tercios en el plazo de un año. Por eso tratamos la obesidad como una enfermedad crónica y armamos un plan de largo plazo, ya sea medicamento continuo, una reducción gradual y estructurada, o una conversación sobre opciones más duraderas.",
  },
  {
    q: "¿Pastilla o inyección?",
    a: "Ambas producen una pérdida de peso promedio comparable en los estudios. La inyección semanal tiene la trayectoria más larga; la pastilla diaria, que se toma con el estómago vacío, le conviene a quien prefiere evitar las agujas. La cobertura del seguro suele diferir entre las dos y lo ayudamos a compararlas.",
  },
  {
    q: "¿Semaglutida o tirzepatida: cuál es mejor?",
    a: "En el único estudio que las comparó de forma directa (SURMOUNT-5), la tirzepatida produjo mayor pérdida de peso promedio: 20.2 por ciento frente a 13.7 por ciento. Pero «mejor» depende de su cobertura, su tolerancia y su perfil de salud; la semaglutida tiene la evidencia más sólida de protección cardiovascular. Recetamos las dos y ajustamos el medicamento al paciente.",
  },
  {
    q: "¿Puedo tomar semaglutida si ya tuve cirugía bariátrica?",
    a: "Muchas veces sí. Los medicamentos GLP-1 pueden ser una herramienta eficaz frente a la recuperación de peso después de una manga o un bypass, y un cirujano bariátrico es quien mejor puede recetarlos en esa situación, porque también puede evaluar si la anatomía está contribuyendo al problema.",
  },
];

export const Route = createFileRoute("/es/semaglutida")({
  head: () => {
    const base = esHead("semaglutide");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              { ...esWebPageSchema("semaglutide"), ...drugAboutAndMainEntity(ES_SEMAGLUTIDE) },
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/semaglutida#faq`,
                inLanguage: "es-US",
                // Preguntas visibles del bloque de FAQ más los encabezados
                // de sección en forma de pregunta.
                mainEntity: allPageFaqs("/es/semaglutida", FAQS).map((f, i) => ({
                  "@type": "Question",
                  ...(i < FAQS.length ? { "@id": `${SITE}/es/semaglutida#faq-${i + 1}` } : {}),
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
              ...drugNodes(ES_SEMAGLUTIDE),
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsSemaglutide,
});

function EsSemaglutide() {
  return (
    <EsPageShell
      page="semaglutide"
      title="Semaglutida (Wegovy® y Ozempic®) para bajar de peso"
      intro="La semaglutida, conocida por las marcas Wegovy® y Ozempic®, transformó la pérdida de peso con medicamentos: en los estudios clínicos ayudó a los pacientes a perder entre el 12 y el 15 por ciento de su peso corporal. En Virginia Bariatric Surgery, en Herndon, la receta y la maneja el Dr. Matthew Fitzer, cirujano bariátrico certificado, no un spa médico ni un cuestionario en internet. Atendemos a pacientes de todo el norte de Virginia y del área metropolitana de Washington, D.C. —incluidos Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria, Sterling, Ashburn, Chantilly y Manassas— en el consultorio y por telemedicina."
    >
      <EsSection id="como-funciona" heading="Qué es la semaglutida y cómo funciona">
        <p>
          La semaglutida imita el GLP-1, una hormona que el intestino produce de forma natural
          después de comer. Al activar esos mismos receptores, hace tres cosas: le indica al cerebro
          que usted está satisfecho, hace más lento el vaciamiento del estómago para que la saciedad
          dure más y ayuda a estabilizar el azúcar en la sangre.
        </p>
        <p>
          Muchos pacientes describen el efecto como el silencio del «ruido de la comida»: esos
          pensamientos constantes sobre qué comer a continuación se calman, y comer menos deja de
          ser una batalla diaria de fuerza de voluntad.
        </p>
      </EsSection>

      <EsSection id="marcas" heading="Wegovy, Ozempic y la nueva pastilla: cuál es la diferencia">
        <p>
          Es el mismo principio activo con aprobaciones distintas. <strong>Wegovy</strong> está
          aprobado específicamente para el control crónico del peso y llega a dosis más altas.{" "}
          <strong>Ozempic</strong> está aprobado para la diabetes tipo 2, aunque produce pérdida de
          peso también. La <strong>pastilla diaria</strong> de semaglutida ofrece resultados
          promedio comparables a la inyección y se toma con el estómago vacío.
        </p>
        <p>
          La diferencia práctica casi siempre es la cobertura del seguro: el mismo paciente puede
          tener aprobada una presentación y negada la otra.
        </p>
      </EsSection>

      <EsSection id="resultados" heading="¿Cuánto peso voy a perder?">
        <p>
          En el estudio STEP 1, los adultos con obesidad que recibieron semaglutida perdieron en
          promedio cerca del 15 por ciento de su peso corporal en 68 semanas, frente a cerca del 2.4
          por ciento con placebo. Para una persona de 250 libras, eso son unas 37 libras en promedio.
        </p>
        <p>
          Los promedios esconden mucha variación. Algunos pacientes pierden bastante más y otros
          responden poco. La alimentación, la actividad física y el sueño siguen influyendo en el
          resultado, incluso con un medicamento eficaz.
        </p>
      </EsSection>

      <EsSection id="beneficios" heading="Beneficios más allá de la báscula">
        <p>
          La semaglutida tiene la evidencia cardiovascular más sólida de este grupo de medicamentos.
          En el estudio SELECT redujo los eventos cardiovasculares mayores, como el infarto y el
          derrame cerebral, en adultos con sobrepeso u obesidad y enfermedad cardiovascular ya
          establecida.
        </p>
        <p>
          También mejora con frecuencia el control del azúcar en la sangre, la presión arterial, los
          triglicéridos y la enfermedad del hígado graso.
        </p>
      </EsSection>

      <EsSection id="candidatos" heading="¿Quién es candidato?">
        <p>
          Adultos con un IMC de 30 o más, o de 27 o más con una enfermedad relacionada con el peso
          como diabetes tipo 2, presión alta o apnea del sueño. No es apropiada si usted o un
          familiar ha tenido carcinoma medular de tiroides o síndrome MEN 2, ni durante el embarazo.
        </p>
      </EsSection>

      <EsSection id="seguridad" heading="Efectos secundarios y por qué importa la supervisión">
        <p>
          Los efectos más comunes son digestivos: náuseas, diarrea, estreñimiento y vómito, sobre
          todo al subir de dosis. Casi siempre son de leves a moderados y ceden con el tiempo. Subir
          la dosis despacio es lo que los mantiene manejables.
        </p>
        <p>
          Los riesgos menos frecuentes pero serios incluyen pancreatitis, enfermedad de la vesícula,
          deshidratación con daño renal y reacciones alérgicas graves. La semaglutida lleva una
          advertencia en recuadro por los tumores de células C de la tiroides observados en ratas;
          no se sabe si ese hallazgo aplica a las personas.
        </p>
        <p>
          Por eso el tratamiento se hace con un médico que lo conoce, no con un frasco comprado por
          internet.
        </p>
      </EsSection>

      <EsSection id="cirujano" heading="¿Por qué recibirla de un cirujano bariátrico?">
        <p>
          Porque vemos el problema completo. Si el medicamento funciona, lo acompañamos a largo
          plazo con apoyo nutricional. Si no funciona lo suficiente, ya estamos en la misma consulta
          donde se conversan las opciones quirúrgicas, sin tener que empezar de cero con otro equipo.
        </p>
        <p>
          La experiencia del Dr. Fitzer con GLP-1 y otros medicamentos para bajar de peso empezó en
          2005, mucho antes de que estos fármacos se volvieran populares.
        </p>
      </EsSection>

      <EsSection id="costo" heading="Costo y cobertura en Virginia">
        <p>
          La cobertura varía enormemente entre planes y muchos exigen autorización previa con
          documentación de intentos anteriores. Nosotros tramitamos esa autorización. Revise los
          requisitos plan por plan en nuestra página de{" "}
          <Link
            to="/es/cobertura-de-seguro"
            className="font-medium text-brand-logo hover:underline"
          >
            cobertura de seguro
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="comparar" heading="Compare con otras opciones">
        <p>
          Vea la{" "}
          <Link to="/es/tirzepatida" className="font-medium text-brand-logo hover:underline">
            tirzepatida
          </Link>
          , la{" "}
          <Link
            to="/es/medicamentos-para-bajar-de-peso"
            className="font-medium text-brand-logo hover:underline"
          >
            guía completa de medicamentos
          </Link>{" "}
          o las opciones quirúrgicas como la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>
          .
        </p>
      </EsSection>
    </EsPageShell>
  );
}
